import { makeStyles } from "@material-ui/core/styles";
import { MuiTheme } from "theme";
import Breakpoints from "common/breakpoints";
import { gradientToBottom } from "utils/css-utils";

interface IStylesProps {
  facilityColor?: string;
  strokeColor?: string;
}

export default makeStyles<MuiTheme, IStylesProps>((theme: MuiTheme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      boxShadow: "none",
      backgroundColor: "transparent"
    },
    [theme.breakpoints.up("md")]: {
      height: 121,
      backgroundColor: theme.palette.common.white,
      padding: 12
    }
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    [theme.breakpoints.up("md")]: {
      marginBottom: 0,
      justifyContent: "center"
    }
  },
  noWorkoutsComponentTitle: {
    color: "#595a5a",
    [theme.breakpoints.up("md")]: {
      fontSize: 14,
      lineHeight: 1,
      color: theme.palette.text.hint
    }
  },
  componentTitle: {
    color: "#595a5a",
    [theme.breakpoints.up("md")]: {
      display: "none"
    }
  },
  componentTitleUpMd: {
    display: "none",
    fontSize: 14,
    color: theme.palette.text.hint,
    lineHeight: 1,
    [theme.breakpoints.up("md")]: {
      display: "block"
    }
  },
  component: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.up("md")]: {
      flexDirection: "column"
    }
  },
  dateWrapper: {
    display: "flex",
    flexDirection: "column",
    width: 84,
    paddingLeft: 18,
    [theme.breakpoints.up("md")]: {
      width: "100%",
      paddingLeft: 0,
      alignItems: "center"
    }
  },
  date: {
    fontFamily: "SuisseIntlMedium, SuisseIntlRegular",
    lineHeight: "12px",
    textTransform: "uppercase",
    position: "relative",
    [theme.breakpoints.up("md")]: {
      fontSize: 24,
      lineHeight: 1,
      marginTop: 12,
      marginBottom: 2,
      color: theme.palette.text.primary
    },
    "&:before": {
      display: "block",
      content: "''",
      width: 9,
      height: 9,
      background: "#222222",
      borderRadius: "50%",
      position: "absolute",
      left: -18,
      top: 2,
      [theme.breakpoints.up("md")]: {
        display: "none"
      }
    }
  },
  day: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    [theme.breakpoints.up("md")]: {
      display: "none"
    }
  },
  card: {
    position: "relative",
    display: "flex",
    paddingLeft: 4,
    marginLeft: 8,
    flexGrow: 1,
    minHeight: 70,
    background: theme.palette.common.white,
    borderRadius: 3,
    boxShadow: "0px 1px 7px 0px rgba(0, 0, 0, 0.1)",
    [theme.breakpoints.up("md")]: {
      minHeight: "auto",
      boxShadow: "none",
      paddingLeft: 0,
      marginLeft: 0,
      width: "100%",
      justifyContent: "center"
    },
    "&:before": {
      position: "absolute",
      left: 0,
      content: "''",
      display: "flex",
      height: "100%",
      flexDirection: "column",
      width: 4,
      borderRadius: "3px 0 0 3px",
      backgroundImage: ({ facilityColor, strokeColor }) =>
        gradientToBottom(facilityColor, strokeColor, 5),
      [theme.breakpoints.up("md")]: {
        display: "none"
      }
    }
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    padding: 10,
    [theme.breakpoints.up("md")]: {
      padding: 0,
      marginLeft: 0,
      width: "100%",
      alignItems: "center"
    }
  },
  titleWrapper: {
    fontSize: 14,
    color: "#222222",
    lineHeight: "normal",
    [theme.breakpoints.down(Breakpoints.SS_320)]: {
      display: "flex",
      flexDirection: "column"
    },
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      whiteSpace: "nowrap"
    }
  },
  title: {
    [theme.breakpoints.up("md")]: {
      display: "none"
    }
  },
  titleDash: {
    [theme.breakpoints.down(Breakpoints.SS_320)]: {
      display: "none"
    },
    [theme.breakpoints.up("md")]: {
      display: "none"
    }
  },
  titleRequestedBy: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "inline"
    }
  },
  facility: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    lineHeight: "normal",
    marginBottom: 4,
    [theme.breakpoints.up("md")]: {
      display: "none"
    }
  },
  commentButton: {
    fontSize: 12,
    color: theme.palette.secondary.dark,
    lineHeight: "normal",
    cursor: "pointer",
    [theme.breakpoints.up("md")]: {
      fontSize: 14,
      lineHeight: 1.5,
      marginTop: 8
    }
  },
  popover: {
    display: "flex",
    flexDirection: "column",
    maxWidth: 300,
    height: "auto"
  },
  popoverTitle: {
    padding: "23px 23px 10px",
    fontFamily: "SuisseIntlMedium, SuisseIntlRegular",
    fontSize: 16,
    borderBottom: "1px solid #eeeeee",
    lineHeight: "16px"
  },
  popoverContent: {
    display: "flex",
    flexDirection: "column",
    padding: 23,
    gap: 36,
    overflowY: "hidden"
  },
  popoverFooter: {
    display: "flex",
    justifyContent: "center",
    paddingBottom: 16
  },
  paginator: {
    display: "flex",
    justifyContent: "center"
  },
  comment: {
    display: "flex",
    flexDirection: "column"
  },
  commentAuthor: {
    fontFamily: "SuisseIntlMedium, SuisseIntlRegular",
    fontSize: 14,
    marginBottom: 4,
    lineHeight: "normal"
  },
  commentDate: {
    textTransform: "uppercase",
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 2,
    lineHeight: "normal"
  },
  commentText: {
    fontSize: 12,
    color: theme.palette.text.primary,
    lineHeight: "15px"
  }
}));
